import { isRichTextDocument } from "./confirmationCardFormatting";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const inline = (text: string): string => escapeHtml(text).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

type Block = { kind: "p"; text: string } | { kind: "ul" | "ol"; items: string[] };

export const plainTextAsHtml = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed || trimmed.startsWith("<") || isRichTextDocument(trimmed)) return text;
  const blocks: Block[] = [];
  for (const line of trimmed.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const kind = bullet ? "ul" : numbered ? "ol" : "p";
    const content = (bullet ?? numbered)?.[1] ?? line.trim();
    const last = blocks[blocks.length - 1];
    if (kind !== "p" && last?.kind === kind) {
      last.items.push(content);
    } else if (kind === "p") {
      blocks.push({ kind, text: content });
    } else {
      blocks.push({ kind, items: [content] });
    }
  }
  return blocks
    .map((block) =>
      block.kind === "p"
        ? `<p>${inline(block.text)}</p>`
        : `<${block.kind}>${block.items.map((item) => `<li><p>${inline(item)}</p></li>`).join("")}</${block.kind}>`,
    )
    .join("");
};

const withDescriptionAsHtml = <T>(item: T): T =>
  isPlainObject(item) && typeof item.description === "string"
    ? ({ ...item, description: plainTextAsHtml(item.description) } as T)
    : item;

const withCriterionDescriptions = (awardCriteria: Record<string, unknown>): Record<string, unknown> => {
  const ratio = awardCriteria.bestPriceQualityRatio;
  if (!isPlainObject(ratio) || !Array.isArray(ratio.criteria)) return awardCriteria;
  const criteria = ratio.criteria.map((criterion) => {
    if (!isPlainObject(criterion)) return criterion;
    const described = withDescriptionAsHtml(criterion);
    return Array.isArray(criterion.subCriteria)
      ? { ...described, subCriteria: criterion.subCriteria.map(withDescriptionAsHtml) }
      : described;
  });
  return { ...awardCriteria, bestPriceQualityRatio: { ...ratio, criteria } };
};

export const withAwardCriterionDescriptionsAsHtml = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map(withAwardCriterionDescriptionsAsHtml) as T;
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      key === "awardCriteria" && isPlainObject(child)
        ? withCriterionDescriptions(child)
        : withAwardCriterionDescriptionsAsHtml(child),
    ]),
  ) as T;
};
